import {
  BookOutlined,
  CheckOutlined,
  RollbackOutlined,
  ShoppingOutlined,
  SolutionOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { ButtonProps, DatePicker, Form, Input, message, Modal, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useCountryContext } from '@/contexts/CountryContext';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPMENT_TYPE } from '@/model/Logistic/Shipment/ShipmentType';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

interface UseShipmentHeaderActionsParams {
  record: Shipment;
  refetch: () => void;
  blockChangeState: { id: string; message: string }[];
  canSend?: boolean;
  isPaid?: boolean;
  showQuantityAlert?: boolean;
  /** Metadata: indica si el archivo tributario fue cargado, para bloquear avance de estado */
  hasTaxFileUploaded?: boolean;
}

/**
 * Hook que encapsula toda la lógica de acciones del header de ShipmentShow:
 * - Botones de cambio de estado según el flujo de envío
 * - Botón de cancelación con motivo
 * - Tooltip de bloqueo de cambio de estado
 * - Context holders para modales y notificaciones
 */
const useShipmentHeaderActions = (params: UseShipmentHeaderActionsParams) => {
  const { isBrasilSelected } = useCountryContext();
  const {
    record,
    refetch,
    blockChangeState,
    canSend,
    isPaid,
    showQuantityAlert,
    hasTaxFileUploaded,
  } = params;
  const { shipmentRepository } = useRepositoriesContext();
  const { hasPermission } = usePermissionContext();
  const { userWorkArea } = useUserContext();
  const { t } = useTranslation();
  const { id, status, has_sale_items, external_document, tracking_number, tipo } = record;
  const shipmentDateRef = useRef<Dayjs | null>(null);
  const shipmentTrackingNumberRef = useRef<string>('');
  const cancelationMotiveRef = useRef<string>('');
  const [modal, contextHolder] = Modal.useModal();
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  const disableStateButtons = blockChangeState.length > 0;
  /**
   * Solo para brasil, obligación legal
   */
  const nonPackageData = isBrasilSelected && record.packages_dimensions?.length === 0;
  // --- Tooltip de bloqueo de cambio de estado (early returns en lugar de ternarios anidados) ---
  const changeStateBlockTooltip = useMemo(() => {
    if (blockChangeState?.length > 0) {
      return `${t('No se puede realizar esta acción')}:\n${blockChangeState.map(bs => bs.message).join('\n')}`;
    }

    if (status === PACKAGE_STATES.PENDIENTE && has_sale_items && !external_document) {
      return t('Para proceder a la gestión de pago debe agregar una orden de venta');
    }

    if (status === PACKAGE_STATES.GESTION_PAGO && has_sale_items && !isPaid) {
      return t('Para proceder a la preparación debe marcar los elementos como pagados');
    }

    if (status === PACKAGE_STATES.PREPARACION && nonPackageData) {
      if (nonPackageData) {
        return t('Falta especificar las dimensiones y el peso del paquete');
      } else if (!canSend) {
        return t('Faltan especificar números de serie o unidades en los elementos del envío');
      }
    }

    /**
     * Bloqueo de avance a PREPARADO si no se cargó archivo tributario.
     * Se evalúa en ADMIN_MANAGEMENT para impedir "Finalizar gestión administrativa" sin el archivo.
     */
    if (status === PACKAGE_STATES.ADMIN_MANAGEMENT && !hasTaxFileUploaded) {
      return t('Debe cargar el archivo tributario antes de finalizar la gestión administrativa');
    }

    return '';
  }, [
    blockChangeState,
    canSend,
    external_document,
    has_sale_items,
    hasTaxFileUploaded,
    isPaid,
    nonPackageData,
    status,
    t,
  ]);
  // --- Visibilidad del botón cancelar ---
  const showCancelButton = useMemo(
    () =>
      (userWorkArea === USER_WORK_AREA.ADMINISTRATION && status === PACKAGE_STATES.GESTION_PAGO) ||
      (hasPermission('edit') &&
        (status === PACKAGE_STATES.PENDIENTE ||
          (userWorkArea === USER_WORK_AREA.LOGISTICS &&
            (status === PACKAGE_STATES.PREPARACION ||
              status === PACKAGE_STATES.PREPARADO ||
              status === PACKAGE_STATES.REPARACION)))),
    [hasPermission, status, userWorkArea],
  );
  // --- Handler del botón cancelar (modal con motivo de cancelación) ---
  const onCancelClick = useCallback(() => {
    cancelationMotiveRef.current = '';
    modal.confirm({
      title: t('¿Estás seguro de que deseas cancelar este envío?'),
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {t('Esta acción no se puede deshacer')}
          <Form.Item layout="vertical" label={t('Motivo de cancelación')} required>
            <Input
              placeholder={t('Motivo de cancelación')}
              onChange={e => {
                cancelationMotiveRef.current = e.target.value;
              }}
            />
          </Form.Item>
        </div>
      ),
      okText: t('Sí, cancelar'),
      okType: 'danger',
      cancelText: t('No'),
      onOk() {
        const cancelationMotive = cancelationMotiveRef.current;

        if (!cancelationMotive || cancelationMotive.trim() === '') {
          message.error(t('Debe indicar un motivo de cancelación'));

          return;
        }

        void shipmentRepository
          .updateShipment(id, {
            status: PACKAGE_STATES.CANCELADO,
            cancellation_motive: cancelationMotive,
          })
          .then(() => {
            void refetch();
          });
      },
    });
  }, [modal, t, shipmentRepository, id, refetch]);
  // --- Props de botones de cambio de estado (early returns por estado) ---
  const changeStateButtonsProps: ButtonProps[] = useMemo(() => {
    // Helper: verificar cantidad antes de ejecutar acción
    const checkQuantity = (stateAction: () => void) => {
      if (showQuantityAlert) {
        modal.confirm({
          title: t('Algunos elementos no tienen cantidad especificada'),
          content: t('¿Desea continuar de todos modos?'),
          okText: t('Sí'),
          okType: 'primary',
          cancelText: t('No'),
          onOk() {
            stateAction();
          },
        });
      } else {
        stateAction();
      }
    };

    // Helper: confirmación simple de cambio de estado (sin inputs adicionales)
    const confirmSimpleStateChange = (
      title: string,
      newState: PACKAGE_STATES,
      options?: { content?: string; okText?: string },
    ) => {
      modal.confirm({
        title: t(title),
        content: options?.content ? t(options.content) : undefined,
        okText: t(options?.okText ?? 'Sí'),
        okType: 'primary',
        cancelText: t('No'),
        onOk() {
          void shipmentRepository.updateShipmentState(id, newState).then(() => {
            void refetch();
          });
        },
      });
    };

    // Helper: botón "Volver a pendiente" reutilizado en PREPARACION y PREPARADO
    const buildBackToPendingButton = (withContent: boolean): ButtonProps | null => {
      if (tipo === SHIPMENT_TYPE.AUTOMATICO || has_sale_items) return null;

      return {
        icon: <RollbackOutlined />,
        onClick: () =>
          confirmSimpleStateChange(
            '¿Estás seguro de que deseas volver esta paquete a pendiente?',
            PACKAGE_STATES.PENDIENTE,
            withContent ? { content: 'Esta acción no se puede deshacer' } : undefined,
          ),
        type: 'default',
        children: t('Volver a pendiente'),
      };
    };

    // Helper: notificaciones post-envío (recepción y orden de servicio creadas)
    const handleShipmentResponse = (data: any) => {
      if (data.reception_created) {
        notificationApi.success({
          message: t('Se ha creado la recepción asociada al envío'),
          description: t('Haga click para abrir la recepción en un nuevo enlace'),
          onClick: () => {
            window.open(`/v2/receptions/show/${data.reception_created}`, '_blank');
          },
        });
      } else if (data.service_order_is_created) {
        notificationApi.success({
          message: t('Se ha creado una orden de servicio asociada al envío'),
          description: t('Haga click para abrir la orden de servicio en un nuevo enlace'),
          onClick: () => {
            window.open(`/v2/${RESOURCES.SERVICE_ORDERS}/show/${data.service_order}`, '_blank');
          },
        });
      } else if (data.service_order_is_created === false) {
        notificationApi.success({
          message: t('Se ha actualizado una orden de servicio pendiente de coordinación'),
          description: t('Haga click para abrir la orden de servicio en un nuevo enlace'),
          onClick: () => {
            window.open(`/v2/${RESOURCES.SERVICE_ORDERS}/show/${data.service_order}`, '_blank');
          },
        });
      }
    };

    if (userWorkArea === USER_WORK_AREA.ADMINISTRATION) {
      // --- Gestión de pago: marcar como pagados (administración) ---
      if (status === PACKAGE_STATES.GESTION_PAGO) {
        return [
          {
            icon: <ShoppingOutlined />,
            onClick: () =>
              confirmSimpleStateChange(
                '¿Estás seguro de que deseas marcar como pagados los elementos?',
                PACKAGE_STATES.PAGADO,
                { content: 'Esta acción no se puede deshacer' },
              ),
            children: t('Marcar como pagados'),
            disabled: !isPaid,
          },
        ];
      }
    }

    if (
      userWorkArea === USER_WORK_AREA.ADMINISTRATION ||
      userWorkArea === USER_WORK_AREA.LOGISTICS
    ) {
      if (status === PACKAGE_STATES.ADMIN_MANAGEMENT) {
        return [
          {
            icon: <BookOutlined />,
            onClick: () =>
              confirmSimpleStateChange(
                '¿Estás seguro de que deseas marcar este paquete como preparado?',
                PACKAGE_STATES.PREPARADO,
              ),
            children: t('Finalizar gestión administrativa'),
            // se bloquea si no se puede enviar O si falta el archivo tributario
            disabled: !canSend || !hasTaxFileUploaded,
          },
        ] as ButtonProps[];
      }
    }

    // --- En viaje: marcar como recibido (logística o atención al cliente) ---
    if (
      status === PACKAGE_STATES.EN_VIAJE &&
      (userWorkArea === USER_WORK_AREA.LOGISTICS ||
        userWorkArea === USER_WORK_AREA.CUSTOMER_SERVICE)
    ) {
      return [
        {
          icon: <CheckOutlined />,
          onClick: () => {
            shipmentDateRef.current = null;
            const changeStateAction = () => {
              void modal.confirm({
                title: t('¿Estás seguro de que deseas marcar como finalizado este envío?'),
                content: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {t('Esta acción no se puede deshacer')}
                    <b>{t('Fecha de recepción del cliente')}:</b>
                    <DatePicker
                      format={'L'}
                      placeholder={t('Seleccionar una fecha')}
                      onChange={date => {
                        shipmentDateRef.current = date;
                      }}
                      maxDate={dayjs()}
                    />
                  </div>
                ),
                okText: t('Sí'),
                okType: 'primary',
                cancelText: t('No'),
                onOk() {
                  const pickedDate = shipmentDateRef.current;

                  if (!pickedDate) {
                    message.error(t('Debe indicar una fecha de envío'));

                    return;
                  }

                  void shipmentRepository
                    .updateShipment(id, {
                      status: PACKAGE_STATES.FINALIZADO,
                      date_finished: pickedDate.toISOString(),
                    })
                    .then(() => {
                      void refetch();
                    });
                },
              });
            };

            checkQuantity(changeStateAction);
          },
          children: t('Marcar recibido'),
        },
      ];
    }

    // Solo logística a partir de aquí
    if (userWorkArea !== USER_WORK_AREA.LOGISTICS) return [];

    // --- Pendiente con elementos de venta: pasar a gestión de pago ---
    if (has_sale_items && status === PACKAGE_STATES.PENDIENTE) {
      return [
        {
          icon: <ShoppingOutlined />,
          onClick: () =>
            confirmSimpleStateChange(
              '¿Estás seguro de que deseas pasar a gestionar el pago de los elementos?',
              PACKAGE_STATES.GESTION_PAGO,
              { content: 'Esta acción no se puede deshacer' },
            ),
          children: t('Pasar a gestión de pago'),
          disabled: !external_document,
        },
      ];
    }

    // --- Reparación, Pendiente o Pagado: comenzar preparación ---
    if (
      status === PACKAGE_STATES.REPARACION ||
      status === PACKAGE_STATES.PENDIENTE ||
      status === PACKAGE_STATES.PAGADO
    ) {
      return [
        {
          icon: <ShoppingOutlined />,
          onClick: () =>
            confirmSimpleStateChange(
              '¿Estás seguro de que deseas preparar este envío?',
              PACKAGE_STATES.PREPARACION,
              { content: 'Esta acción no se puede deshacer', okText: 'Sí, preparar' },
            ),
          children: t('Comenzar preparación'),
        },
      ];
    }

    // --- En preparación: finalizar preparación ---
    if (status === PACKAGE_STATES.PREPARACION) {
      if (isBrasilSelected) {
        return [
          buildBackToPendingButton(false),
          {
            icon: <SolutionOutlined />,
            onClick: () =>
              confirmSimpleStateChange(
                '¿Estás seguro de que deseas enviar este paquete a gestión administrativa?',
                PACKAGE_STATES.ADMIN_MANAGEMENT,
              ),
            children: t('Enviar a gestión administrativa'),
            disabled: nonPackageData,
          },
        ].filter(Boolean) as ButtonProps[];
      } else {
        return [
          buildBackToPendingButton(false),
          {
            icon: <BookOutlined />,
            onClick: () =>
              confirmSimpleStateChange(
                '¿Estás seguro de que deseas marcar este paquete como preparado?',
                PACKAGE_STATES.PREPARADO,
              ),
            children: t('Finalizar preparación'),
            disabled: !canSend,
          },
        ].filter(Boolean) as ButtonProps[];
      }
    }

    // --- Preparado: enviar ---
    if (status === PACKAGE_STATES.PREPARADO) {
      return [
        buildBackToPendingButton(true),
        {
          icon: <TruckOutlined />,
          onClick: () => {
            shipmentDateRef.current = null;
            shipmentTrackingNumberRef.current = tracking_number || '';
            const changeStateAction = () => {
              void modal.confirm({
                title: t('¿Estás seguro de que deseas marcar como enviado?'),
                content: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {t('Esta acción no se puede deshacer')}
                    <b>{t('Fecha de envío')}:</b>
                    <DatePicker
                      format={'L'}
                      placeholder={t('Seleccionar una fecha')}
                      onChange={date => {
                        shipmentDateRef.current = date;
                      }}
                      maxDate={dayjs()}
                    />
                    <b>{t('Número de seguimiento')}:</b>
                    <Input
                      placeholder={t('Introducir número de seguimiento')}
                      onChange={v => {
                        shipmentTrackingNumberRef.current = v.target.value;
                      }}
                      defaultValue={shipmentTrackingNumberRef.current}
                    />
                  </div>
                ),
                okText: t('Sí'),
                okType: 'primary',
                cancelText: t('No'),
                onOk() {
                  const pickedDate = shipmentDateRef.current;

                  if (!pickedDate) {
                    message.error(t('Debe indicar una fecha de envío'));

                    return;
                  }

                  void shipmentRepository
                    .updateShipment(id, {
                      status: PACKAGE_STATES.EN_VIAJE,
                      date: pickedDate.toISOString(),
                      tracking_number: shipmentTrackingNumberRef.current ?? undefined,
                    })
                    .then(data => {
                      handleShipmentResponse(data);
                      void refetch();
                    });
                },
              });
            };

            checkQuantity(changeStateAction);
          },
          children: t('Enviar'),
          disabled: !canSend,
        },
      ].filter(Boolean) as ButtonProps[];
    }

    return [];
  }, [
    status,
    userWorkArea,
    has_sale_items,
    t,
    canSend,
    hasTaxFileUploaded,
    isPaid,
    isBrasilSelected,
    nonPackageData,
    showQuantityAlert,
    modal,
    shipmentRepository,
    id,
    refetch,
    external_document,
    tipo,
    tracking_number,
    notificationApi,
  ]);

  return {
    changeStateButtonsProps,
    changeStateBlockTooltip,
    disableStateButtons,
    showCancelButton,
    onCancelClick,
    contextHolder,
    notificationContextHolder,
  };
};

export default useShipmentHeaderActions;
